import React, { FunctionComponent, useState, useCallback, useEffect } from 'react';

import _ from 'lodash';
import { Alert, Button, Col, Form, Modal, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { GroupId } from 'admin/groups/Groups';
import { isAuthorized } from 'auth/Auth';
import { ConfirmModal } from 'common/confirm-modal/ConfirmModal';
import { DropdownDataWithHeader } from 'common/dropdown-picker/DropdownPicker';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { MetaData, MetaDataTag } from 'common/response-editor/MetadataParser';
import {
    ResponseEditor,
    ResponseEditorWidgetPlacement,
    Props as ResponseEditorOptions,
} from 'common/response-editor/ResponseEditor';
import { VariableType } from 'common/variables/Variables';
import { Overlay } from 'core/overlay/Overlay';
import { ButtonVariant } from 'core/util/Bootstrap';
import { SearchRequest } from 'core/util/Search';
import { getHashtagTextsFromMetaData, getTextFromMetaData, hasHashtagsInMetaData } from 'social/common/util/Util';
import { ErrorDisplay } from 'social/publisher/error-display/ErrorDisplay';
import { LocationTagSelector } from 'social/publisher/location-tag-selector/LocationTagSelector';
import { PostTarget } from 'social/publisher/post-target/PostTarget';
import {
    KeyPhrase,
    KeyPhraseType,
    MediaUrlValidation,
    PublisherError,
    SelectedSource,
    MentionData,
    FacebookTargetFields,
    LocationTag,
    HashtagData,
    HashtagSearchType,
    HashtagGroup,
    SourceName,
    MediaPathsToAltText,
    ValidateMentionResponse,
} from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import {
    fetchKeyPhrases,
    fetchMention,
    fetchHashtag,
    fetchRelatedHashtag,
    fetchHashtagGroups,
    saveHashtagGroup,
    deleteHashtagGroup,
    updateHashtagGroup,
    validateMention,
} from 'social/publisher/PublisherFetchers';
import { getSourceKeysFromSelectedSources, isFacebookTargetFieldsSameAsDefault } from 'social/publisher/util/Util';
import { MAX_CHARS_BY_SOURCE } from 'social/publisher/validator/Validator';
import 'social/publisher/response-box/ResponseBox.scss';

interface Props {
    tenantId: number | undefined;
    agencyId: number | undefined;
    selectedSources: SelectedSource;
    content: MetaData[];
    media: any[];
    mediaValidation: MediaUrlValidation[];
    responseEditorRef: any;
    publisherErrors: PublisherError[];
    isMentionEnabled: boolean;
    isHashtagEnabled: boolean;
    isPostTargetEnabled: boolean;
    targetAudience: { FACEBOOK: FacebookTargetFields } | undefined;
    isContentAndMediaLocked: boolean;
    isLocationTagEnabled: boolean;
    locationTag: LocationTag | null;
    isDMLinkEnabled: boolean;
    customizeSourceKey: SourceName | 'ALL_SOURCES';
    mediaPathsToAltText?: MediaPathsToAltText;
}

interface CustomLabelForMentionsProps {
    tenantId: number | undefined;
    responseEditorRef: any;
    content: MetaData[];
    source: string;
    sourceDisplayName: string;
    linkRootPath: string;
}

interface HashtagGroupFormInputs {
    groupName: string;
    hashtags: string;
}

interface AltTextFormInputs {
    altText: string;
}

// These are for updating the PublisherContext periodically to significantly reduce input lag.
// This timer gets reset everytime text is updated until the user stops typing long enough to complete the interval
let textChangeTimer: number;
const TYPING_UPDATE_INTERVAL = 500;
const ACCEPTED_FILE_TYPES =
    'image/jpg, image/jpeg, image/png, image/bmp, image/webp, image/tif, image/tiff, video/mpeg, video/webm, video/quicktime, video/mp4, video/mov, video/wmv, video/avi, video/mpe, video/mpeg4, video/mpg, video/qt, video/asf, video/mkv, video/3g2, video/3gp, video/3gpp, video/dat, video/divx, video/dv, video/f4v, video/m2ts, video/m4v, video/mod, video/mts, video/nsv, video/ogm, video/ogv, video/tod, video/ts, video/vob';
const MENTION_PICKER_OVERLAY_AREA = 'social-publisher-mention-picker';
const HASHTAG_PICKER_OVERLAY_AREA = 'social-publisher-hashtag-picker';
const ADD_EDIT_HASHTAG_GROUP_OVERLAY_AREA = 'social-publisher-add-edit-hashtag-group';
const EDIT_ALT_TEXT_OVERLAY_AREA = 'social-publisher-edit-alt-text';

const GetCustomLabelForMentions: FunctionComponent<CustomLabelForMentionsProps> = ({
    tenantId,
    responseEditorRef,
    content,
    source,
    sourceDisplayName,
    linkRootPath,
}): JSX.Element => {
    const { t } = useTranslation(['social/publisher']);
    const [mentionInputText, setMentionInputText] = useState('');
    const [isMentionTextValid, setIsMentionTextValid] = useState(true);

    const getValidatedMention = async (text: string, source: string): Promise<ValidateMentionResponse> => {
        const validateMentionResponse = await trackPromise(
            validateMention(tenantId, text, source),
            `validate-mention-area-${source}`,
        );

        return validateMentionResponse;
    };

    const handleApplyMention = async (e: any) => {
        e.stopPropagation();
        const validatedMention = await getValidatedMention(mentionInputText, source);

        setIsMentionTextValid(validatedMention.isValid);

        if (validatedMention.isValid) {
            const newContent: MetaData[] = content || [];

            newContent.push({
                text: validatedMention.displayName || mentionInputText,
                attributes: {
                    additionalInfo: {
                        source,
                        link: `${linkRootPath}/${mentionInputText}`,
                        externalId: validatedMention.externalId,
                    },
                },
                tags: [MetaDataTag.Mention],
            });

            newContent.push({
                text: ' ',
                attributes: {},
                tags: [],
            });

            if (responseEditorRef.current.setContents) {
                responseEditorRef.current.setContents(newContent);
            }

            setMentionInputText('');
        }
    };

    return (
        <div className="padding5-10">
            <Row>
                <Col xs={8}>
                    <Form.Control
                        placeholder={t('MENTION_INPUT_PLACEHOLDER')}
                        onChange={(e): void => {
                            setMentionInputText(e.target.value);
                        }}
                        onClick={(e: any): void => {
                            e.stopPropagation();
                        }}
                        type="text"
                    />

                    {!isMentionTextValid && (
                        <div className="orange top5">
                            <i className="icon icon-exclamation-circle right5" />
                            {t('MENTIONED_PAGE_NOT_FOUND', {
                                source: sourceDisplayName,
                            })}
                        </div>
                    )}
                </Col>

                <Col xs={4}>
                    <Button variant={ButtonVariant.Primary} onClick={handleApplyMention}>
                        {t('APPLY')}
                    </Button>
                </Col>
            </Row>
            <Overlay area={`validate-mention-area-${source}`} />
        </div>
    );
};

export const ResponseBox: FunctionComponent<Props> = ({
    tenantId,
    agencyId,
    selectedSources,
    content,
    media,
    responseEditorRef,
    publisherErrors,
    isMentionEnabled,
    isHashtagEnabled,
    isPostTargetEnabled,
    targetAudience,
    isContentAndMediaLocked,
    isLocationTagEnabled,
    locationTag,
    isDMLinkEnabled,
    customizeSourceKey,
    mediaPathsToAltText,
}) => {
    const dispatch = usePublisherDispatch();
    const { t } = useTranslation(['social/publisher']);
    const [, dispatchMessage] = useMessage();
    const [sourceText, setSourceText] = useState(getTextFromMetaData(content));
    const [profanePhrases, setProfanePhrases] = useState('');
    const [rejectedPhrases, setRejectedPhrases] = useState('');
    const [isDMLinkAdded, setIsDMLinkAdded] = useState(false);
    const [showAddEditHashtagGroupModal, setShowAddEditHashtagGroupModal] = useState(false);
    const [showAltTextEditModal, setShowAltTextEditModal] = useState(false);
    const [showConfirmDeleteHashtagGroupModal, setShowConfirmDeleteHashtagGroupModal] = useState(false);
    const [selectedHashtagGroup, setSelectedHashtagGroup] = useState<HashtagGroup | null>(null);
    const [selectedMediaUrl, setSelectedMediaUrl] = useState<string>('');
    const { register, handleSubmit, errors } = useForm();

    const canAddHashtagGroup = isAuthorized({ actions: [ActionId.HashtagGroup.Add] });
    const canEditHashtagGroup = isAuthorized({ actions: [ActionId.HashtagGroup.Edit] });
    const canDeleteHashtagGroup = isAuthorized({ actions: [ActionId.HashtagGroup.Delete] });

    const calculateMaxChars = (selectedSources: SelectedSource): number | undefined => {
        if (customizeSourceKey !== 'ALL_SOURCES') {
            return MAX_CHARS_BY_SOURCE[customizeSourceKey];
        }

        const charsArr: number[] = [];

        Object.keys(selectedSources).forEach((source) => {
            let maxChars = MAX_CHARS_BY_SOURCE[source];

            if (isDMLinkAdded) {
                maxChars -= 23;
            }
            if (selectedSources[source] && maxChars) {
                charsArr.push(maxChars);
            }
        });
        return _.min(charsArr);
    };

    const updateText = (metadata: MetaData[]): void => {
        dispatch({
            type: 'set-content',
            payload: {
                value: metadata,
            },
        });

        // Resets the Content errors
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'CONTENT' } });
    };

    const handleTextChange = (responseEditorObj: any): void => {
        // We do this because triggering the dispatch on the fly is _insanely_ laggy.
        // This allows us update in one go when the user's done typing.
        if (textChangeTimer) {
            clearTimeout(textChangeTimer);
        }
        textChangeTimer = window.setTimeout(() => {
            updateText(responseEditorObj.metadata);
            setProfanePhrases('');
            setRejectedPhrases('');
        }, TYPING_UPDATE_INTERVAL);
    };

    const handleFilesAdded = async (currentFiles: any[], allFiles: any[]): Promise<any[]> => {
        dispatch({
            type: 'set-media',
            payload: {
                value: allFiles,
            },
        });

        // Resets the Media errors
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'MEDIA' } });

        return allFiles || [];
    };

    const handleMediaDelete = (index: any, allFiles: any[]): void => {
        dispatch({
            type: 'set-media',
            payload: {
                value: allFiles,
            },
        });

        // Resets the Media errors
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'MEDIA' } });
    };

    const handleFilesReordered = (allFiles: any[]): void => {
        dispatch({
            type: 'set-media',
            payload: {
                value: allFiles,
            },
        });

        // Resets the Media errors
        dispatch({ type: 'remove-publisher-errors-by-component', payload: { value: 'MEDIA' } });
    };

    const handleUploadProgress = (inProgress: boolean, progressValue: number): void => {
        /* TODO: This is kind of a hack because we have no good way of tracking individual upload sessions,
         * just the general progress from the same handler that's called periodically.
         * It always starts at 5, so we'll check that as a "start" value and assume progress happening. */
        if (inProgress && progressValue === 5) {
            dispatch({
                type: 'add-promises-in-transit',
                payload: {
                    value: 'MEDIA_UPLOAD',
                },
            });
        } else if (!inProgress) {
            dispatch({
                type: 'remove-promises-in-transit',
                payload: {
                    value: 'MEDIA_UPLOAD',
                },
            });
        }
    };

    const getMentionDropdownData = (mentionData: MentionData): DropdownDataWithHeader[] => {
        const dropdownData: DropdownDataWithHeader[] = [];

        if (mentionData.facebookPages) {
            // need to pass link in additionalInfo so that it will not get interpreted as a link element
            const facebookOptions = mentionData.facebookPages.map((el) => {
                return {
                    label: el.name,
                    customLabelElement: (
                        <div className="facebook-mention-option">
                            <span className="facebook-mention-name">{el.name}</span>
                            {el.verificationStatus === 'blue_verified' && (
                                <i className="icon icon-check-circle social-status-verified" />
                            )}
                        </div>
                    ),
                    additionalInfo: { source: 'FACEBOOK', externalId: el.id, link: el.link },
                };
            });

            dropdownData.push({
                header: t('FACEBOOK'),
                content: facebookOptions,
                icon: 'social-icon facebook',
            });
        }
        if (mentionData.twitterProfiles) {
            const twitterOptions = mentionData.twitterProfiles.map((el) => {
                return {
                    label: `@${el.screenname}`,
                    customLabelElement: (
                        <div className="twitter-mention-option">
                            <span className="twitter-mention-name">{el.name}</span>
                            <span className="twitter-mention-screenname">{`@${el.screenname}`}</span>
                            {el.verificationStatus && <i className="icon icon-check-circle social-status-verified" />}
                        </div>
                    ),
                    additionalInfo: { source: 'TWITTER', externalId: el.screenname, link: el.link },
                };
            });

            dropdownData.push({
                header: t('TWITTER'),
                content: twitterOptions,
                icon: 'social-icon twitter',
            });
        }

        if (selectedSources.INSTAGRAM) {
            dropdownData.push({
                header: t('INSTAGRAM'),
                content: [
                    {
                        label: 'Instagram', // no need to translate, this won't show on the UI (but this needs to be non-empty for this item to show up)
                        customLabelElement: (
                            <GetCustomLabelForMentions
                                tenantId={tenantId}
                                responseEditorRef={responseEditorRef}
                                content={content}
                                source="INSTAGRAM"
                                sourceDisplayName="Instagram" // todo: refactor to pass in source object
                                linkRootPath="https://instagram.com"
                            />
                        ),
                    },
                ],
                icon: 'social-icon instagram',
            });
        }

        if (selectedSources.LINKEDIN) {
            dropdownData.push({
                header: t('LINKEDIN'),
                content: [
                    {
                        label: 'LinkedIn', // no need to translate, this won't show on the UI (but this needs to be non-empty for this item to show up)
                        customLabelElement: (
                            <GetCustomLabelForMentions
                                tenantId={tenantId}
                                responseEditorRef={responseEditorRef}
                                content={content}
                                source="LINKEDIN"
                                sourceDisplayName="LinkedIn" // todo: refactor to pass in source object
                                linkRootPath="https://www.linkedin.com/company"
                            />
                        ),
                    },
                ],
                icon: 'social-icon linkedin',
            });
        }

        return dropdownData;
    };

    const getMentions = async (searchString?: string): Promise<DropdownDataWithHeader[]> => {
        const selectedSourceKeys = getSourceKeysFromSelectedSources(selectedSources);
        let dropdownData: DropdownDataWithHeader[] = [];
        let query = 'a';

        if (searchString && searchString.trim().length > 0) {
            query = searchString.trim();
        }

        if (selectedSourceKeys.length > 0) {
            const mentionData = await trackPromise(
                fetchMention(tenantId, query, selectedSourceKeys),
                MENTION_PICKER_OVERLAY_AREA,
            );

            dropdownData = getMentionDropdownData(mentionData);
        }

        return dropdownData;
    };

    const handleMentionClick = (mentionData: any): void => {
        const {
            additionalInfo: { link },
        } = mentionData;

        if (link) {
            window.open(link, '_blank');
        }
    };

    const handleHashtagGroupClick = (hashtagGroup: HashtagGroup): void => {
        const newContent: MetaData[] = content || [];

        _.forEach(hashtagGroup.hashtags, (hashtag) => {
            newContent.push({
                text: `${hashtag}`,
                attributes: { additionalInfo: { link: `https://twitter.com/search?q=${hashtag}` } },
                tags: [MetaDataTag.HashTag],
            });

            newContent.push({
                text: ' ',
                attributes: {},
                tags: [],
            });
        });

        if (responseEditorRef.current.setContents) {
            responseEditorRef.current.setContents(newContent);
        }
    };

    const getHashtagGroupDropdownData = (hashtagGroups: HashtagGroup[]): DropdownDataWithHeader[] => {
        const dropdownData: DropdownDataWithHeader[] = [];

        if (hashtagGroups && hashtagGroups.length > 0) {
            const hashtagGroupsOptions = hashtagGroups.map((el) => {
                let description = '';

                if (el.hashtags && el.hashtags.length > 0) {
                    description = el.hashtags.join(' ');

                    if (description.length > 100) {
                        description = `${description.substring(0, 100)}...`;
                    }
                }

                return {
                    label: el.name,
                    customLabelElement: (
                        <div
                            className="hashtag-group pointer relative"
                            role="presentation"
                            onClick={(e): void => {
                                e.stopPropagation();
                                handleHashtagGroupClick(el);
                            }}
                        >
                            <div>
                                <div>{el.name}</div>
                                <div className="grey4">{description}</div>
                            </div>

                            <div className="abs-top-right actions">
                                {canEditHashtagGroup && (
                                    <i
                                        className="icon-pencil"
                                        role="presentation"
                                        onClick={(e): void => {
                                            e.stopPropagation();
                                            setSelectedHashtagGroup(el);
                                            setShowAddEditHashtagGroupModal(true);
                                        }}
                                    />
                                )}
                                {canDeleteHashtagGroup && (
                                    <i
                                        className="icon-trash-alt left10"
                                        role="presentation"
                                        onClick={(e): void => {
                                            e.stopPropagation();
                                            setSelectedHashtagGroup(el);
                                            setShowConfirmDeleteHashtagGroupModal(true);
                                        }}
                                    />
                                )}
                            </div>
                        </div>
                    ),
                    additionalInfo: {
                        hahstags: el.hashtags,
                    },
                };
            });

            dropdownData.push({
                header: t('HASHTAG_GROUPS'),
                customHeaderElement: getCustomHeaderForHashtags(t('HASHTAG_GROUPS'), t('ADD_HASHTAG_GROUP_LINK_TEXT')),
                content: hashtagGroupsOptions,
            });
        } else if (canAddHashtagGroup) {
            dropdownData.push({
                header: t('HASHTAG_GROUPS'),
                content: [
                    {
                        label: 'No hashtag groups', // no need to translate, this won't show on the UI (but this needs to be non-empty for this item to show up)
                        customLabelElement: getCustomLabelWhenNoHahstagGroupsExist(),
                    },
                ],
            });
        }

        return dropdownData;
    };

    const getHashtagGroups = async (searchString?: string): Promise<DropdownDataWithHeader[]> => {
        let dropdownData: DropdownDataWithHeader[] = [];

        const search: SearchRequest = {
            filters: [],
            start: 0,
        };

        const searchHashtagGroupResponse = await trackPromise(
            fetchHashtagGroups(tenantId, search),
            HASHTAG_PICKER_OVERLAY_AREA,
        );

        let { hashtagGroups } = searchHashtagGroupResponse;

        // filter based on the group's tags or the group name
        if (hashtagGroups && searchString) {
            hashtagGroups = _.filter(hashtagGroups, (hashtagGroup) => {
                const hashtagMatch = _.some(hashtagGroup.hashtags, (hashtag) => {
                    return hashtag.toLowerCase().startsWith(`#${searchString.toLowerCase()}`);
                });

                return hashtagGroup.name.toLowerCase().startsWith(searchString.toLowerCase()) || hashtagMatch;
            });
        }

        dropdownData = getHashtagGroupDropdownData(hashtagGroups);

        return dropdownData;
    };

    const getHashtagDropdownData = (hashtagData: HashtagData): DropdownDataWithHeader[] => {
        const dropdownData: DropdownDataWithHeader[] = [];

        if (hashtagData.hashtags) {
            // need to pass link in additionalInfo so that it will not get interpreted as a link element
            const hashtagOptions = hashtagData.hashtags.map((el) => {
                return {
                    label: el.text,
                    customLabelElement: (
                        <div className="hashtag-option">
                            <span className="hashtag-name">{el.text}</span>
                        </div>
                    ),
                    additionalInfo: {
                        // source: 'TWITTER', // for now all hashtags are from twitter
                        posts: el.posts,
                        likes: el.likes,
                        weight: el.weight,
                        link: `https://twitter.com/search?q=${el.text}`,
                    },
                };
            });

            dropdownData.push({
                header: t('HASHTAGS'),
                content: hashtagOptions,
            });
        }

        return dropdownData;
    };

    const getHashtags = async (searchString?: string): Promise<DropdownDataWithHeader[]> => {
        let dropdownData: DropdownDataWithHeader[] = [];
        let query = '';

        if (searchString && searchString.trim().length > 0) {
            query = searchString.trim();
        }

        let currentHashtags: string[] = [];

        if (hasHashtagsInMetaData(content)) {
            currentHashtags = getHashtagTextsFromMetaData(content);
        }

        if (currentHashtags.length > 0) {
            const hashtagData = await trackPromise(
                fetchRelatedHashtag(tenantId, currentHashtags, HashtagSearchType.TEXT),
                HASHTAG_PICKER_OVERLAY_AREA,
            );

            dropdownData = getHashtagDropdownData(hashtagData); // todo: if no related hashtags, then get recent hashtags?
        } else {
            const hashtagData = await trackPromise(
                fetchHashtag(tenantId, query, HashtagSearchType.TEXT),
                HASHTAG_PICKER_OVERLAY_AREA,
            );

            dropdownData = getHashtagDropdownData(hashtagData);
        }

        return dropdownData;
    };

    const getHashtagsAndHashtagGroups = async (searchString?: string): Promise<DropdownDataWithHeader[]> => {
        const hashtags = await getHashtags(searchString);
        const hashtagGroups = await getHashtagGroups(searchString);

        let dropdownData: DropdownDataWithHeader[] = [];

        dropdownData = dropdownData.concat(hashtags);
        dropdownData = dropdownData.concat(hashtagGroups);

        return dropdownData;
    };

    const handleHashtagClick = (hashtagData: any): void => {
        const {
            additionalInfo: { link },
        } = hashtagData;

        if (link) {
            window.open(link, '_blank');
        }
    };

    const getCustomHeaderForHashtags = (heading: string, linkText: string): JSX.Element => {
        return (
            <div className="bg-grey8 padding5-10">
                <Row>
                    <Col xs={6}>
                        <div className="bold top5">{heading}</div>
                    </Col>

                    <Col xs={6} className="text-right">
                        {canAddHashtagGroup && (
                            <Button
                                variant={ButtonVariant.Link}
                                active
                                onClick={(): void => setShowAddEditHashtagGroupModal(true)}
                                className="padding-right0"
                            >
                                {linkText}
                            </Button>
                        )}
                    </Col>
                </Row>
            </div>
        );
    };

    const getCustomLabelWhenNoHahstagGroupsExist = (): JSX.Element => {
        return (
            <div
                role="presentation"
                onClick={(e): void => {
                    e.stopPropagation();
                }}
            >
                <Button
                    variant={ButtonVariant.Link}
                    active
                    onClick={(e): void => {
                        setShowAddEditHashtagGroupModal(true);
                    }}
                    className="padding0"
                >
                    {t('ADD_HASHTAG_GROUP_LINK_TEXT')}
                </Button>
            </div>
        );
    };

    const handleAltTextClick = (mediaUrl: string): void => {
        setSelectedMediaUrl(mediaUrl);
        setShowAltTextEditModal(true);
    };

    const hideAltTextEditModal = (): void => {
        setShowAltTextEditModal(false);
    };

    const editAltText = (data: AltTextFormInputs): void => {
        dispatch({
            type: 'set-media-path-to-alt-text',
            payload: { value: { mediaPath: selectedMediaUrl, altText: data.altText } },
        });
        setShowAltTextEditModal(false);
    };

    const findKeyPhrases = useCallback(async (tenantId: number, text: string): Promise<void> => {
        const keyPhrases = await fetchKeyPhrases(tenantId, text);
        const getKeyPhrasesDisplayByType = (phrases: KeyPhrase[], type: KeyPhraseType): string => {
            return _.uniq(
                _.map(
                    _.filter(phrases, (el) => el.keyPhraseType === type),
                    'keyPhraseText',
                ),
            ).join(', ');
        };
        const newProfanePhrases = getKeyPhrasesDisplayByType(keyPhrases, KeyPhraseType.Profane);
        const newRejectedPhrases = getKeyPhrasesDisplayByType(keyPhrases, KeyPhraseType.Rejected);

        setProfanePhrases(newProfanePhrases);
        setRejectedPhrases(newRejectedPhrases);
    }, []);

    // Note: React Quill onBlur event bug: https://stackoverflow.com/questions/47018722/react-quill-onblur-not-working
    const handleOnBlur = (): void => {
        const newSourceText = getTextFromMetaData(content);

        setSourceText(newSourceText);
    };

    const getHeader = (): JSX.Element => {
        let header = <h3 className="response-box-title">{t('COMPOSE_MESSAGE')}</h3>;

        if (selectedSources.YOUTUBE) {
            header = (
                <h3 className="response-box-title">
                    <span>{t('DESCRIPTION')}</span>
                    <OverlayTrigger
                        placement="right"
                        overlay={<Tooltip id="youtube-description-tooltip">{t('YOUTUBE_DESCRIPTION_INFO')}</Tooltip>}
                    >
                        <i className="icon-info-circle response-box-title-icon" />
                    </OverlayTrigger>
                </h3>
            );
        }
        return header;
    };

    // todo: only works when instantiate
    const getPlaceholder = (): string => {
        let placeholder = 'WHAT_DO_YOU_WANT_TO_SHARE';

        if (selectedSources.YOUTUBE) {
            placeholder = 'WHAT_IS_YOUR_VIDEO_ABOUT';
        }
        return t(placeholder);
    };

    const TargetIcon: FunctionComponent = (): JSX.Element => {
        return <i className="icon-bullseye-arrow" />;
    };

    const LocationIcon: FunctionComponent = (): JSX.Element => {
        return <i className="icon-map-marker-alt" />;
    };

    const dmLinkIcon: FunctionComponent = (): JSX.Element => {
        return <i className="icon-envelope" />;
    };

    const dragAndDropEl = (
        <div className="response-box-dnd-message">
            <i className="icon icon-upload" />
            <Trans
                t={t}
                i18nKey="DROP_FILES_HERE_TO_ATTACH"
                components={{
                    a: (
                        <button
                            type="button"
                            className="r4e-social-browse-media-btn btn btn-link"
                            onClick={(e): void => e.preventDefault()}
                        >
                            &nbsp;
                        </button>
                    ),
                }}
            />
        </div>
    );

    const responseEditorOptions: ResponseEditorOptions = {
        placeholder: getPlaceholder(),
        textOptions: {
            defaultText: content,
            displayMaxChar: true,
            maxCharLimit: calculateMaxChars(selectedSources),
            twitterSelected: selectedSources.TWITTER,
        },
        toolBarOptions: {
            enable: true,
            richTextOptions: {
                enableRichText: true,
                options: {
                    enableBold: false,
                    enableItalic: false,
                    enableUnderline: false,
                    enableStrikeThrough: false,
                    enableBlockQuote: false,
                    enableCodeBlock: false,
                    enableOrderedList: false,
                    enableBulletList: false,
                    enableLink: true,
                },
            },
            specialOptions: {
                templateOptions: {
                    enable: true,
                    expandTemplateRequest: {
                        moduleGroup: [GroupId.Social],
                        url: 'true', // Yes, this should be a string.
                    },
                    extractVariablesTypes: [VariableType.LOCATION],
                },
                variableOptions: {
                    enable: true,
                    variableTypeAndObjectIDs: [
                        {
                            variableType: VariableType.SOURCE,
                        },
                        {
                            variableType: VariableType.TENANT,
                        },
                        {
                            variableType: VariableType.LOCATION,
                        },
                    ],
                    variableBasic: true,
                    excludeVariables: new Set(['city', 'location']),
                },
                mentionOptions: {
                    enable: isMentionEnabled,
                    handlers: {
                        onSearchTextChange: getMentions,
                        onOpen: getMentions,
                        onClick: handleMentionClick,
                    },
                    overlayArea: MENTION_PICKER_OVERLAY_AREA,
                    includePrefix: false,
                    displayOptions: {
                        displayCategories: true,
                    },
                },
                hashTagOptions: {
                    enable: isHashtagEnabled,
                    handlers: {
                        onSearchTextChange: getHashtagsAndHashtagGroups,
                        onOpen: getHashtagsAndHashtagGroups,
                        onClick: handleHashtagClick,
                    },
                    overlayArea: HASHTAG_PICKER_OVERLAY_AREA,
                    includePrefix: false,
                    displayOptions: {
                        displayCategories: true,
                    },
                },
                emojiOptions: {
                    enable: true,
                },
                mediaOptions: {
                    accept: ACCEPTED_FILE_TYPES,
                    customIcon: <i className="icon-images" />,
                    enable: true,
                    canReOrder: true,
                    canEditImage: true,
                    displayDropContainer: true,
                    dropContainerInfoElement: dragAndDropEl,
                    media,
                    onUploadProgress: handleUploadProgress,
                    onMediaDelete: handleMediaDelete,
                    onFilesAccepted: handleFilesAdded,
                    onMediaReOrder: handleFilesReordered,
                    onMediaAltTextClick: handleAltTextClick,
                    selectedSources,
                    replaceEditedImage: true,
                    maxFiles: 40,
                },
            },
            respondButtonOptions: {
                enable: false,
                text: 'RESPOND',
                disabled: false,
            },
            customOptions: [],
        },
        menuBarOptions: {
            enable: false,
        },
        enableRespondOnEnterKey: false,
        widgetPlacement: ResponseEditorWidgetPlacement.Bottom,
        handlers: {
            onTextChange: handleTextChange,
            onBlur: handleOnBlur,
        },
        tenantId: tenantId || -1,
        agencyId: agencyId || -1,
    };

    if (isPostTargetEnabled) {
        let defaultIsActive = false;

        if (
            selectedSources.FACEBOOK &&
            targetAudience &&
            targetAudience.FACEBOOK &&
            !isFacebookTargetFieldsSameAsDefault(targetAudience.FACEBOOK)
        ) {
            defaultIsActive = true;
        }
        responseEditorOptions.toolBarOptions!.customOptions!.push({
            tooltip: t('TARGET_YOUR_POST'),
            iconComponent: TargetIcon,
            modalComponent: PostTarget,
            closeModalOnOutsideClick: true,
            defaultIsActive,
        });
    }

    if (isLocationTagEnabled) {
        let defaultIsActive = false;

        if (selectedSources.TWITTER && locationTag) {
            defaultIsActive = true;
        }
        responseEditorOptions.toolBarOptions!.customOptions!.push({
            tooltip: t('TAG_A_LOCATION'),
            iconComponent: LocationIcon,
            modalComponent: LocationTagSelector,
            closeModalOnOutsideClick: true,
            defaultIsActive,
        });
    }

    if (isDMLinkEnabled && selectedSources.TWITTER) {
        responseEditorOptions.toolBarOptions!.customOptions!.push({
            tooltip: t('ADD_DIRECT_MESSAGE_LINK'),
            iconComponent: dmLinkIcon,
            onIconClick: (handleActive) => {
                const dmLinkAdded = !isDMLinkAdded;

                setIsDMLinkAdded(dmLinkAdded);
                handleActive(dmLinkAdded);

                dispatch({
                    type: 'set-is-dm-link-added',
                    payload: { value: dmLinkAdded },
                });
            },
            defaultIsActive: false,
        });
    }

    useEffect((): void => {
        if (tenantId && sourceText) {
            findKeyPhrases(tenantId, sourceText);
        } else {
            setProfanePhrases('');
            setRejectedPhrases('');
        }
    }, [tenantId, sourceText, findKeyPhrases]);

    const addEditHashtagGroup = async (data: HashtagGroupFormInputs): Promise<void> => {
        try {
            if (!data.hashtags || data.hashtags.length === 0) {
                return;
            }

            const hashtags = data.hashtags
                .split(/[ ,]+/)
                .filter((hashtag) => hashtag.startsWith('#') && hashtag.length > 1)
                .map((hashtag) => hashtag.trim());

            const hashtagGroup = {
                id: selectedHashtagGroup ? selectedHashtagGroup.id : '',
                name: data.groupName,
                hashtags,
            };

            if (selectedHashtagGroup) {
                const response = await trackPromise(
                    updateHashtagGroup(tenantId, hashtagGroup),
                    ADD_EDIT_HASHTAG_GROUP_OVERLAY_AREA,
                );

                if (response) {
                    setShowAddEditHashtagGroupModal(false);
                    dispatchMessage({
                        type: MessageActionType.ShowSuccess,
                        text: t('HASHTAG_GROUP_UPDATED_SUCCESSFULLY'),
                    });
                }
            } else {
                const response = await trackPromise(
                    saveHashtagGroup(tenantId, hashtagGroup),
                    ADD_EDIT_HASHTAG_GROUP_OVERLAY_AREA,
                );

                if (response?.hashtagGroup?.id) {
                    setShowAddEditHashtagGroupModal(false);
                    dispatchMessage({
                        type: MessageActionType.ShowSuccess,
                        text: t('HASHTAG_GROUP_CREATED_SUCCESSFULLY'),
                    });
                } // todo: consider showing an error message in an else clause here
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error?.detail?.[0]?.message;

            if (errorMessage) {
                dispatchMessage({
                    type: MessageActionType.ShowError,
                    text: errorMessage,
                });
            }
        }
    };

    const hideAddEditHashtagGroupModal = (): void => {
        setShowAddEditHashtagGroupModal(false);
        setSelectedHashtagGroup(null);
    };

    const confimDeleteHashtagGroup = async (hashtagGroupId: string): Promise<void> => {
        try {
            const response = await trackPromise(
                deleteHashtagGroup(tenantId, hashtagGroupId),
                HASHTAG_PICKER_OVERLAY_AREA,
            );

            if (response) {
                dispatchMessage({
                    type: MessageActionType.ShowSuccess,
                    text: t('HASHTAG_GROUP_DELETED_SUCCESSFULLY'),
                });
            }
        } catch (error) {
            dispatchMessage({
                type: MessageActionType.ShowError,
                text: t('HASHTAG_GROUP_DELETE_FAILED'),
            });
        }
    };

    const areHashtagsValid = (hashtags: string): string | boolean => {
        if (!hashtags || hashtags.length === 0) {
            return false;
        }

        const hashtagList = hashtags.trim().split(/[ ,]+/);

        // are all hashtags valid?
        let areAllHashtagsValid = true;

        _.forEach(hashtagList, (hashtag) => {
            if (!/^#[^ !@#$%^&*(),.?":{}|<>]*$/gi.test(hashtag.trim())) {
                areAllHashtagsValid = false;
            }
        });

        if (!areAllHashtagsValid) {
            return t('VALIDATION_HASHTAG_GROUP_INVALID_HASHTAGS') as string;
        }

        // are there 30 or less hashtags
        if (hashtagList.length > 30) {
            return t('VALIDATION_HASHTAG_GROUP_MAX_30_HASHTAGS') as string;
        }

        return true;
    };

    return (
        <div className="r4e-social-response-box">
            {getHeader()}
            <ErrorDisplay component="CONTENT" publisherErrors={publisherErrors} />
            <ResponseEditor
                ref={responseEditorRef}
                tenantId={responseEditorOptions.tenantId}
                agencyId={responseEditorOptions.agencyId}
                placeholder={responseEditorOptions.placeholder}
                textOptions={responseEditorOptions.textOptions}
                menuBarOptions={responseEditorOptions.menuBarOptions}
                toolBarOptions={responseEditorOptions.toolBarOptions}
                enableRespondOnEnterKey={responseEditorOptions.enableRespondOnEnterKey}
                widgetPlacement={responseEditorOptions.widgetPlacement}
                handlers={responseEditorOptions.handlers}
                canEdit={!isContentAndMediaLocked}
            />

            <div className="key-phrases-alert">
                {profanePhrases.length > 0 && (
                    <Alert variant="danger">
                        <i className="icon-exclamation-circle" />
                        <span>{t('ERROR_PROFANITY_PHRASES', { phrases: profanePhrases })}</span>
                    </Alert>
                )}
                {rejectedPhrases.length > 0 && (
                    <Alert variant="warning">
                        <i className="icon-exclamation-triangle" />
                        <span>{t('WARNING_GUIDELINE_PHRASES', { phrases: rejectedPhrases })}</span>
                    </Alert>
                )}
            </div>

            <Modal
                show={showAddEditHashtagGroupModal}
                onHide={hideAddEditHashtagGroupModal}
                className="r4e-social-add-edit-hashtag-group-modal"
            >
                <Form noValidate onSubmit={handleSubmit(addEditHashtagGroup)}>
                    <Overlay area={ADD_EDIT_HASHTAG_GROUP_OVERLAY_AREA} />
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {selectedHashtagGroup ? t('EDIT_HASHTAG_GROUP') : t('ADD_HASHTAG_GROUP')}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Label>{t('GROUP_NAME')}</Form.Label>
                            <Form.Control
                                name="groupName"
                                type="text"
                                placeholder={t('HASHTAG_GROUP_NAME_PLACEHOLDER')}
                                defaultValue={selectedHashtagGroup ? selectedHashtagGroup.name : ''}
                                ref={register({
                                    required: t('VALIDATION_HASHTAG_GROUP_NAME_REQUIRED') as string,
                                })}
                                isInvalid={!!errors?.groupName}
                            />
                            <Form.Control.Feedback type="invalid">{errors.groupName?.message}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group>
                            <Form.Label>{t('HASHTAGS')}</Form.Label>
                            <Form.Control
                                name="hashtags"
                                as="textarea"
                                placeholder={t('HASHTAG_GROUP_HASHTAGS_PLACEHOLDER')}
                                style={{ height: '150px' }}
                                defaultValue={selectedHashtagGroup ? selectedHashtagGroup.hashtags.join(' ') : ''}
                                ref={register({
                                    required: t('VALIDATION_HASHTAG_GROUP_HASHTAGS_REQUIRED') as string,
                                    validate: areHashtagsValid,
                                })}
                                isInvalid={!!errors?.hashtags}
                            />
                            <Form.Control.Feedback type="invalid">{errors.hashtags?.message}</Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={hideAddEditHashtagGroupModal}>
                            {t('CANCEL')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('SAVE')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {showConfirmDeleteHashtagGroupModal && selectedHashtagGroup && (
                <ConfirmModal
                    title={t('DELETE_HASHTAG_GROUP')}
                    body={t('CONFIRM_DELETE_HASHTAG_GROUP', {
                        groupName: selectedHashtagGroup.name,
                        hashtags: selectedHashtagGroup.hashtags.join(' '),
                    })}
                    confirmButtonLabel={t('DELETE')}
                    cancelButtonLabel={t('CANCEL')}
                    confirm={(): void => {
                        confimDeleteHashtagGroup(selectedHashtagGroup!.id);
                        setShowConfirmDeleteHashtagGroupModal(false);
                    }}
                    cancel={(): void => setShowConfirmDeleteHashtagGroupModal(false)}
                />
            )}

            <Modal show={showAltTextEditModal} onHide={hideAltTextEditModal} className="r4e-social-alt-text-edit-modal">
                <Form noValidate onSubmit={handleSubmit(editAltText)}>
                    <Overlay area={EDIT_ALT_TEXT_OVERLAY_AREA} />
                    <Modal.Header closeButton>
                        <Modal.Title>{t('ALTERNATIVE_TEXT')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group>
                            <Form.Control
                                name="altText"
                                type="text"
                                placeholder={t('ALTERNATIVE_TEXT_PLACEHOLDER')}
                                defaultValue={
                                    mediaPathsToAltText && mediaPathsToAltText[selectedMediaUrl]
                                        ? mediaPathsToAltText[selectedMediaUrl]
                                        : ''
                                }
                                ref={register({})}
                                isInvalid={false}
                            />
                            <Form.Text className="text-muted">{t('ALTERNATIVE_TEXT_CONTEXT')}</Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={hideAltTextEditModal}>
                            {t('CANCEL')}
                        </Button>
                        <Button variant="primary" type="submit">
                            {t('SAVE')}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};
